import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../api"; 
import { toast } from "react-toastify";

const CreateVoucherPage = () => {
    const [projectId, setProjectId] = useState(null);
    const [selectedBeneficiaries, setSelectedBeneficiaries] = useState([]);
    const [allBeneficiaries, setAllBeneficiaries] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(""); 
    const [showBeneficiaryPopup, setShowBeneficiaryPopup] = useState(false);
    const [selectedVendorIds, setSelectedVendorIds] = useState([]);
    const [allVendors, setAllVendors] = useState([]);
    
    const [isVendorDropdownOpen, setIsVendorDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const fetchAllVendors = useCallback(async (projId) => {
        try {
            const response = await api.get(`/vres/projects/${projId}/vendors`);
            setAllVendors(response.data || []);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to fetch vendors.");
        }
    }, []);

    const fetchBeneficiaries = useCallback(async (projId) => {
        try {
            const response = await api.get(`/vres/projects/${projId}/approved-beneficiaries`);
            setAllBeneficiaries(response.data || []);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to fetch beneficiaries.");
        }
    }, []);

    useEffect(() => {
        const savedProjectId = localStorage.getItem("selectedProjectId");
        if (savedProjectId) {
            setProjectId(savedProjectId);
            fetchBeneficiaries(savedProjectId);
            fetchAllVendors(savedProjectId);
        } else {
            const noProjectMsg = "No project selected. Please select a project first.";
            toast.error(noProjectMsg);
            setError(noProjectMsg);
        }
    }, [fetchBeneficiaries, fetchAllVendors]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsVendorDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const toggleVendor = (vendorId) => {
        setSelectedVendorIds((prev) => {
            if (prev.includes(vendorId)) {
                return prev.filter((id) => id !== vendorId);
            } else {
                return [...prev, vendorId];
            }
        });
    };

    const getSelectedVendorNames = () => {
        if (selectedVendorIds.length === 0) return "Select Vendors";
        const names = allVendors
            .filter(v => selectedVendorIds.includes(v.userId))
            .map(v => v.name);
        return names.join(", ");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!projectId || selectedBeneficiaries.length === 0) {
            toast.error("Please select beneficiaries.");
            return;
        }
        setIsLoading(true);

        const payload = {
            beneficiaryIds: selectedBeneficiaries.map((b) => b.beneficiaryId),
            vendors: selectedVendorIds,
        };

        try {
            const response = await api.post(`/vres/projects/${projectId}/vouchers`, payload);
            toast.success(response.data.message || "Vouchers issued successfully!");
            handleCancel(); 
        } catch (err) {
            console.error("Voucher creation error:", err);
            let displayMessage = "Failed to issue vouchers. Please try again.";

            if (err.response && err.response.data) {
                const backendData = err.response.data;
                let backendMessage = "";
                if (backendData && typeof backendData === 'object' && backendData.message) {
                    backendMessage = backendData.message;
                } else if (typeof backendData === 'string') {
                    backendMessage = backendData;
                }

                if (backendMessage) {
                    displayMessage = `Voucher creation failed: ${backendMessage}`;
                } else if (err.response.status === 403) {
                    displayMessage = "Voucher creation failed: You do not have permission.";
                } else {
                    displayMessage = `Voucher creation failed: Request failed with status code ${err.response.status}.`;
                }
            } else if (err.message) {
                displayMessage = `Voucher creation failed: ${err.message}`;
            }

            setError(displayMessage);
            toast.error(displayMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setSelectedBeneficiaries([]);
        setSelectedVendorIds([]);
        setError("");
    };

    // --- Select All Logic ---
    const handleSelectAllBeneficiaries = (e) => {
        if (e.target.checked) {
            // Select all approved beneficiaries
            setSelectedBeneficiaries(allBeneficiaries);
        } else {
            // Clear selection
            setSelectedBeneficiaries([]);
        }
    };

    const toggleBeneficiarySelection = (beneficiary) => {
        setSelectedBeneficiaries((prev) => {
            const exists = prev.some((b) => b.beneficiaryId === beneficiary.beneficiaryId);
            return exists
                ? prev.filter((b) => b.beneficiaryId !== beneficiary.beneficiaryId)
                : [...prev, beneficiary];
        });
    };

    // Helper: Check if all are selected
    const isAllBeneficiariesSelected = allBeneficiaries.length > 0 && selectedBeneficiaries.length === allBeneficiaries.length;

    return (
        <div style={styles.container}>
            <h2 style={styles.heading}>Create Voucher</h2>
            {error && <div style={{ color: "red", marginBottom: 15, textAlign: 'center', fontWeight: 'bold' }}>{error}</div>}
            <form onSubmit={handleSubmit} style={styles.form}>
                
                <div style={styles.formGroup}>
                    <label style={styles.label}>Beneficiaries</label>
                    <button type="button" onClick={() => setShowBeneficiaryPopup(true)} style={styles.btnSelect}>Select Beneficiaries ({selectedBeneficiaries.length})</button>
                </div>

                <div style={styles.formGroup} ref={dropdownRef}>
                    <label style={styles.label}>Select Vendors</label>
                    <div style={{ position: 'relative' }}>
                        <div 
                            style={styles.dropdownHeader} 
                            onClick={() => setIsVendorDropdownOpen(!isVendorDropdownOpen)}
                        >
                            <span style={styles.selectedText}>
                                {getSelectedVendorNames()}
                            </span>
                            <span style={styles.arrow}>{isVendorDropdownOpen ? '▲' : '▼'}</span>
                        </div>

                        {isVendorDropdownOpen && (
                            <div style={styles.dropdownList}>
                                {allVendors.length > 0 ? (
                                    allVendors.map((vendor) => {
                                        const isSelected = selectedVendorIds.includes(vendor.userId);
                                        return (
                                            <div 
                                                key={vendor.userId} 
                                                onClick={() => toggleVendor(vendor.userId)}
                                                style={{
                                                    ...styles.vendorOption,
                                                    backgroundColor: isSelected ? "#e7f1ff" : "#fff",
                                                    color: isSelected ? "#0056b3" : "#333"
                                                }}
                                            >
                                                <input 
                                                    type="checkbox" 
                                                    checked={isSelected} 
                                                    readOnly 
                                                    style={{ marginRight: "10px", cursor: "pointer" }}
                                                />
                                                {vendor.name}
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div style={{ padding: "10px", color: "#777" }}>No vendors available.</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div style={styles.buttonGroup}>
                    <button type="submit" disabled={isLoading} style={styles.btnPrimary}>
                        {isLoading ? "Submitting..." : "Issue Voucher"}
                    </button>
                    <button type="button" onClick={handleCancel} style={styles.btnSecondary}>Cancel</button>
                </div>
            </form>

            {showBeneficiaryPopup && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <h3>Select Beneficiaries</h3>
                        {allBeneficiaries.length > 0 ? (
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        <th style={styles.tableHeader}>
                                            {/* --- Select All Checkbox --- */}
                                            <input 
                                                type="checkbox" 
                                                onChange={handleSelectAllBeneficiaries}
                                                checked={isAllBeneficiariesSelected}
                                                style={{ cursor: "pointer" }}
                                            />
                                            <span style={{ marginLeft: "8px", fontSize: "0.9em", fontWeight: "normal" }}>Select All</span>
                                        </th>
                                        <th style={styles.tableHeader}>Name</th>
                                        <th style={styles.tableHeader}>Phone</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {allBeneficiaries.map((b) => (
                                        <tr key={b.beneficiaryId}>
                                            <td style={styles.tableCell}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedBeneficiaries.some((sel) => sel.beneficiaryId === b.beneficiaryId)}
                                                    onChange={() => toggleBeneficiarySelection(b)}
                                                />
                                            </td>
                                            <td style={styles.tableCell}>{b.name}</td>
                                            <td style={styles.tableCell}>{b.phone}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p>No approved beneficiaries found for this project.</p>
                        )}
                        <div style={{display: "flex", justifyContent: "flex-end", marginTop: 20}}>
                            <button onClick={() => setShowBeneficiaryPopup(false)} style={styles.btnPrimary}>Done</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: { maxWidth: 700, margin: "auto", padding: 30, backgroundColor: "#fff", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" },
    heading: { textAlign: "center", color: "#007bff", marginBottom: 24 },
    form: { display: "grid", gridTemplateColumns: "1fr", gap: "16px" }, 
    formGroup: { width: "100%" },
    label: { fontWeight: "bold", marginBottom: 8, display: "block" },
    input: { width: "100%", padding: 10, borderRadius: 4, border: "1px solid #ced4da", boxSizing: "border-box" },
    btnSelect: { padding: "10px 20px", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: 4, cursor: "pointer" },
    buttonGroup: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 },
    btnPrimary: { padding: "10px 20px", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: 4, cursor: "pointer" },
    btnSecondary: { padding: "10px 20px", backgroundColor: "#6c757d", color: "white", border: "none", borderRadius: 4, cursor: "pointer" },
    modalOverlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
    modalContent: { background: "white", padding: 30, borderRadius: 8, width: "90%", maxWidth: "600px", maxHeight: "80%", overflowY: "auto" },
    tableHeader: { borderBottom: "2px solid #dee2e6", padding: "12px", textAlign: "left" },
    tableCell: { borderBottom: "1px solid #dee2e6", padding: "12px" },

    dropdownHeader: {
        width: "100%",
        padding: "10px",
        borderRadius: "4px",
        border: "1px solid #ced4da",
        backgroundColor: "white",
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        boxSizing: "border-box",
        minHeight: "42px"
    },
    selectedText: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        marginRight: "10px",
        flex: 1
    },
    dropdownList: {
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        backgroundColor: "#fff",
        border: "1px solid #ced4da",
        borderRadius: "0 0 4px 4px",
        maxHeight: "200px",
        overflowY: "auto",
        zIndex: 1000,
        boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
    },
    vendorOption: {
        padding: "10px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        borderBottom: "1px solid #f1f1f1",
        transition: "background-color 0.2s"
    },
    arrow: {
        fontSize: "12px",
        color: "#666"
    }
};

export default CreateVoucherPage;